<template>
  <intersect
    root-margin="50px 0px 0px"
    @enter.once="mountFlickity"
  >
    <div :class="['flickity-carousel', { 'flickity-carousel--is-mounted': isMounted }]">
      <slot />
    </div>
  </intersect>
</template>

<script>
import Intersect from 'vue-intersect';

export default {
  components: {
    Intersect,
  },
  props: {
    useIntersection: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    pause: {
      type: Boolean,
    },
    select: {
      type: Number,
      default: -1,
    },
    // Should be a varizing size (ie. window.innerWidth)
    resize: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      defaultOptions: {
        cellAlign: 'center',
        contain: false,
        autoPlay: 7000,
        wrapAround: true,
        dragThreshold: 1,
        friction: 0.8,
        selectedAttraction: 0.2,
        arrowShape: 'M66.51 0.58C67.35 -0.21 68.66 -0.19 69.45 0.62C70.22 1.44 70.22 2.71 69.45 3.52C66.36 6.61 50.87 22.1 23.01 49.96C50.87 77.84 66.36 93.33 69.45 96.42C70.29 97.22 70.31 98.55 69.49 99.36C68.7 100.2 67.37 100.22 66.56 99.4C66.54 99.38 66.51 99.36 66.51 99.36C61.72 94.57 23.38 56.23 18.59 51.44C17.78 50.63 17.78 49.31 18.59 48.5C28.18 38.92 61.72 5.37 66.51 0.58Z',
        lazyLoad: 1,
        // Custom option, not part of the flickity library
        hidePrevNextButtons: false,
      },
      isMounted: false,
    };
  },
  watch: {
    pause(val) {
      if (val) {
        this.flickity.pausePlayer();
      } else {
        this.flickity.unpausePlayer();
      }
    },
    select(val) {
      if (val > -1) {
        this.flickity.select(val);
      }
    },
    resize() {
      // Resizing window can sometimes make wierd image ratios for the slides.
      this.flickity.destroy();
      this.initFlickity();
    },
  },
  mounted() {
    if (!this.useIntersection) {
      this.mountFlickity();
    }
  },
  methods: {
    mountFlickity() {
      if (!this.isMounted) {
        this.isMounted = true;
        this.initFlickity();
        this.attachResizeEvent();
      }
    },
    initFlickity() {
      const Flickity = require('flickity'); // eslint-disable-line global-require
      this.$nextTick(function mountCarousel() {
        const options = Object.assign(this.defaultOptions, this.options);

        this.flickity = new Flickity(this.$el, options);
        this.setPrevNextButtonsVisibility();

        this.flickity.resize();

        this.flickity.on('change', (slideIndex) => {
          this.$emit('on-change', slideIndex);
        });

        // Start paused if requested
        if (this.pause) {
          this.flickity.pausePlayer();
        }
      });
    },
    attachResizeEvent() {
      let timeout;

      // Fix SSR issue
      if (typeof window === 'undefined') {
        return;
      }

      window.addEventListener('resize', () => {
        // Debounce. Only execute the code when the resizing has finished
        clearTimeout(timeout);

        timeout = setTimeout(() => {
          this.setPrevNextButtonsVisibility();
        }, 100);
      });
    },
    setPrevNextButtonsVisibility() {
      if (!this.options.hidePrevNextButtons) {
        return;
      }

      // Force flickity to update its UI, this sets the buttons state (disabled)
      this.flickity.resize();
      const prevButton = this.$el.querySelector('.flickity-prev-next-button.previous');
      const nextButton = this.$el.querySelector('.flickity-prev-next-button.next');

      if (prevButton.disabled && nextButton.disabled) {
        prevButton.classList.add('d-none');
        nextButton.classList.add('d-none');
      } else {
        prevButton.classList.remove('d-none');
        nextButton.classList.remove('d-none');
      }
    },
  },
};
</script>

<style src='flickity/dist/flickity.css'></style>

<style scoped>
.flickity-carousel {
  position: relative;
}

/* hide all but the first slide until we're 100% ready to rock */
.flickity-carousel /deep/ .carousel-cell:not(:first-child) {
  display: none;
}

/* once we're mounted, unleash the cells */
.flickity-carousel--is-mounted /deep/ .flickity-slider .carousel-cell {
  display: block;
}

.flickity-sumo-buttons /deep/ .flickity-button,
.flickity-sumo-buttons /deep/ .flickity-button:focus {
  width: 40px;
  height: 40px;
  background: #09292b;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.flickity-sumo-buttons /deep/ .flickity-button.previous {
  left: -20px;
}

.flickity-sumo-buttons /deep/ .flickity-button.next {
  right: -20px;
}

.flickity-sumo-buttons /deep/ .flickity-button svg {
  top: 30%;
  left: 30%;
  width: 40%;
  height: 40%;
}

.flickity-sumo-buttons /deep/ .flickity-button svg path {
  fill: #fff;
}
</style>
